import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

const COLLECTION_NAME = 'notifications';

/**
 * Notification types
 */
export const NOTIFICATION_TYPES = {
  VOLUNTEER_PENDING: 'volunteer_pending',
  VOLUNTEER_APPROVED: 'volunteer_approved',
  VOLUNTEER_REJECTED: 'volunteer_rejected',
  EVENT_CREATED: 'event_created',
  EVENT_UPDATED: 'event_updated',
  EVENT_CANCELLED: 'event_cancelled',
  ASSIGNMENT_CONFIRMED: 'assignment_confirmed',
  ASSIGNMENT_CANCELLED: 'assignment_cancelled',
  GENERAL: 'general',
};

/**
 * Create a notification
 * @param {Object} notificationData - Notification data
 * @returns {Promise<string>} - Notification ID
 */
export const createNotification = async (notificationData) => {
  try {
    const notificationRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...notificationData,
      read: false,
      createdAt: serverTimestamp(),
    });
    return notificationRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw new Error('Failed to create notification: ' + error.message);
  }
};

/**
 * Get notifications for a user
 * @param {string} userId - User ID
 * @param {number} limitCount - Max number of notifications to fetch
 * @returns {Promise<Array>} - Array of notifications
 */
export const getUserNotifications = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId)
      // Removed: orderBy('createdAt', 'desc')
      // Will sort in memory to avoid composite index requirement
    );

    const querySnapshot = await getDocs(q);
    const notifications = [];

    querySnapshot.forEach((doc) => {
      notifications.push({
        notificationId: doc.id,
        ...doc.data(),
      });
    });

    // Sort by createdAt in memory (newest first)
    notifications.sort((a, b) => {
      const timeA = a.createdAt?.toMillis?.() || 0;
      const timeB = b.createdAt?.toMillis?.() || 0;
      return timeB - timeA;
    });

    // Apply limit after sorting
    return notifications.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting notifications:', error);
    throw new Error('Failed to get notifications: ' + error.message);
  }
};

/**
 * Get unread notification count
 * @param {string} userId - User ID
 * @returns {Promise<number>} - Count of unread notifications
 */
export const getUnreadCount = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const markAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, COLLECTION_NAME, notificationId);
    await updateDoc(notificationRef, {
      read: true,
      readAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw new Error('Failed to mark notification as read: ' + error.message);
  }
};

/**
 * Dismiss (delete) a notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const dismissNotification = async (notificationId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, notificationId));
  } catch (error) {
    console.error('Error dismissing notification:', error);
    throw new Error('Failed to dismiss notification: ' + error.message);
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const markAllAsRead = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    const querySnapshot = await getDocs(q);
    const updatePromises = [];

    querySnapshot.forEach((doc) => {
      updatePromises.push(
        updateDoc(doc.ref, {
          read: true,
          readAt: serverTimestamp(),
        })
      );
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking all as read:', error);
    throw new Error('Failed to mark all as read: ' + error.message);
  }
};

/**
 * Subscribe to notifications in real-time
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function to handle notifications
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToNotifications = (userId, callback) => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId)
    // Removed: orderBy('createdAt', 'desc') and limit
    // Will sort and limit in memory to avoid composite index requirement
  );

  return onSnapshot(q, (snapshot) => {
    const notifications = [];
    snapshot.forEach((doc) => {
      notifications.push({
        notificationId: doc.id,
        ...doc.data(),
      });
    });

    // Sort by createdAt in memory (newest first)
    notifications.sort((a, b) => {
      const timeA = a.createdAt?.toMillis?.() || 0;
      const timeB = b.createdAt?.toMillis?.() || 0;
      return timeB - timeA;
    });

    // Apply limit after sorting
    const limitedNotifications = notifications.slice(0, 50);

    callback(limitedNotifications);
  });
};

/**
 * Helper: Notify volunteer approval status
 * @param {string} userId - Volunteer user ID
 * @param {boolean} approved - Whether approved or rejected
 * @param {string} organizationName - Organization name
 */
export const notifyVolunteerApproval = async (userId, approved, organizationName) => {
  await createNotification({
    userId,
    type: approved ? NOTIFICATION_TYPES.VOLUNTEER_APPROVED : NOTIFICATION_TYPES.VOLUNTEER_REJECTED,
    title: approved ? 'Account Approved!' : 'Account Update',
    message: approved
      ? `Your volunteer account for ${organizationName} has been approved. You can now sign up for events!`
      : `Your volunteer application for ${organizationName} was not approved. Please contact the organization admin.`,
    actionUrl: approved ? '/dashboard' : null,
  });
};

/**
 * Helper: Notify org admins of pending volunteer
 * @param {Array} adminIds - Array of admin user IDs
 * @param {string} volunteerName - Volunteer's name
 * @param {string} volunteerId - Volunteer's ID
 */
export const notifyPendingVolunteer = async (adminIds, volunteerName, volunteerId) => {
  const promises = adminIds.map((adminId) =>
    createNotification({
      userId: adminId,
      type: NOTIFICATION_TYPES.VOLUNTEER_PENDING,
      title: 'New Volunteer Pending Approval',
      message: `${volunteerName} has requested to join your organization.`,
      actionUrl: '/dashboard',
      actionData: { volunteerId },
    })
  );
  await Promise.all(promises);
};

/**
 * Helper: Notify volunteers of new event
 * @param {Array} volunteerIds - Array of volunteer user IDs
 * @param {string} eventName - Event name
 * @param {string} eventId - Event ID
 */
export const notifyNewEvent = async (volunteerIds, eventName, eventId) => {
  const promises = volunteerIds.map((volunteerId) =>
    createNotification({
      userId: volunteerId,
      type: NOTIFICATION_TYPES.EVENT_CREATED,
      title: 'New Event Available',
      message: `A new event "${eventName}" is now available for sign-up!`,
      actionUrl: `/events/${eventId}`,
    })
  );
  await Promise.all(promises);
};

/**
 * Helper: Notify user of assignment confirmation
 * @param {string} userId - User ID
 * @param {string} eventName - Event name
 * @param {string} assignmentName - Assignment/role name
 * @param {string} eventId - Event ID
 */
export const notifyAssignmentConfirmation = async (userId, eventName, assignmentName, eventId) => {
  await createNotification({
    userId,
    type: NOTIFICATION_TYPES.ASSIGNMENT_CONFIRMED,
    title: 'Assignment Confirmed',
    message: `You've been assigned to "${assignmentName}" for ${eventName}.`,
    actionUrl: `/events/${eventId}`,
  });
};

/**
 * Send a new-event announcement email to all active volunteers in the organization.
 * Calls the `sendEventAnnouncement` Cloud Function.
 * @param {string} eventId - The event to announce
 * @returns {Promise<{ sent: number, failed: number }>}
 */
export const sendEventAnnouncement = async (eventId, mode = 'new_event') => {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = httpsCallable(functions, 'sendEventAnnouncement');
  const result = await fn({ eventId, mode });
  return result.data;
};

/**
 * Helper: Create notification for all platform admins
 * @param {Object} notificationData - Notification data (type, title, message, metadata)
 * @returns {Promise<void>}
 */
export const createNotificationForPlatformAdmins = async (notificationData) => {
  try {
    // Query platform admins directly
    const snapshot = await getDocs(query(collection(db, 'users'), where('userType', '==', 'platform-admin')));
    const platformAdmins = snapshot.docs.map((d) => ({ uid: d.id, ...d.data() }));

    if (platformAdmins.length === 0) {
      console.warn('No platform admins found to notify');
      return;
    }

    // Create notification for each platform admin
    const promises = platformAdmins.map((admin) =>
      createNotification({
        userId: admin.uid,
        type: notificationData.type || NOTIFICATION_TYPES.GENERAL,
        title: notificationData.title,
        message: notificationData.message,
        actionUrl: notificationData.actionUrl || '/notifications',
        metadata: notificationData.metadata || {},
      })
    );

    await Promise.all(promises);
    console.log(`Created notifications for ${platformAdmins.length} platform admin(s)`);
  } catch (error) {
    console.error('Error creating notifications for platform admins:', error);
    throw new Error('Failed to notify platform admins: ' + error.message);
  }
};
